import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Star, RefreshCw, Loader2, TrendingUp, TrendingDown, Target,
  Activity, Award, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Activity, label: 'Operations Executive', href: '/operations/executive' },
  { icon: Award, label: 'Payer Contracts', href: '/admin/payer-vbc' },
  { icon: BarChart3, label: 'VBC Quality Measures', active: true },
];

function StarRow({ stars }) {
  return (
    <span className="inline-flex" aria-label={`${stars} stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`w-3.5 h-3.5 ${n <= stars ? 'text-amber-400 fill-amber-400' : 'text-cn-border'}`} />
      ))}
    </span>
  );
}

export default function AdminVbcQualityMeasures() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [scope, setScope] = useState('global');
  const [contractId, setContractId] = useState('');
  const [loading, setLoading] = useState(true);

  const loadContracts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/payer-contracts`, getAuthHeader());
      setContracts(r.data?.contracts || r.data || []);
    } catch (e) { /* ignore */ }
  }, [getAuthHeader]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = scope === 'global'
        ? `${API}/admin/vbc/measures`
        : `${API}/admin/vbc/measures/by-contract/${contractId}`;
      const r = await axios.get(url, getAuthHeader());
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load VBC measures');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, scope, contractId]);

  useEffect(() => { loadContracts(); }, [loadContracts]);
  useEffect(() => {
    if (scope === 'global' || (scope === 'contract' && contractId)) load();
  }, [load, scope, contractId]);

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="VBC Quality Measures"
      subtitle="HEDIS / Stars-style measure rollup. Without these, your Value-Based-Care contracts don't pay shared-savings or quality bonuses."
      testId="admin-vbc-measures-page"
    >
      <div className="cn-card p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="vbc-controls">
        <div className="flex items-center gap-2">
          <span className="text-xs text-cn-text-muted font-semibold">Scope</span>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="border border-cn-border rounded-lg px-2 py-1.5 text-sm bg-white"
            data-testid="vbc-scope-select"
          >
            <option value="global" data-testid="vbc-scope-option-global">Global population</option>
            <option value="contract" data-testid="vbc-scope-option-contract">By contract</option>
          </select>
        </div>
        {scope === 'contract' && (
          <div className="flex items-center gap-2">
            <select
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
              className="border border-cn-border rounded-lg px-2 py-1.5 text-sm bg-white max-w-xs"
              data-testid="vbc-contract-select"
            >
              <option value="">Select contract…</option>
              {contracts.map((c) => (
                <option key={c.id} value={c.id}>{c.payer_name} · {c.tier || c.stage}</option>
              ))}
            </select>
          </div>
        )}
        <Button onClick={load} disabled={loading} className="cn-btn cn-btn-ghost !py-2 ml-auto" data-testid="vbc-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {loading && <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>}

      {!loading && data && (
        <>
          {data.contract && (
            <div className="cn-card p-4 mb-4 bg-cn-bone-50/40 border-cn-border" data-testid="vbc-contract-banner">
              <p className="text-xs text-cn-text-muted">Contract scope</p>
              <p className="font-display font-semibold text-cn-text">
                {data.contract.payer_name} · {data.contract.tier} · {(data.contract.lives || 0).toLocaleString()} attributed lives
              </p>
            </div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="vbc-summary">
            <KpiTile
              label="Weighted Stars"
              value={`${(data.summary?.weighted_stars || 0).toFixed(2)} ★`}
              icon={Star}
              tone="amber"
              testid="vbc-summary-stars"
            />
            <KpiTile
              label="Measures eligible"
              value={`${data.summary?.measures_eligible || 0} / ${data.summary?.measures_total || 0}`}
              icon={Target}
              tone="neutral"
              testid="vbc-summary-eligible"
            />
            <KpiTile
              label="5-star measures"
              value={data.summary?.five_star_count || 0}
              icon={TrendingUp}
              tone="emerald"
              testid="vbc-summary-5star"
            />
            <KpiTile
              label="3-star or better"
              value={data.summary?.three_star_or_better || 0}
              icon={Award}
              tone="emerald"
              testid="vbc-summary-3star"
            />
          </div>

          <div className="cn-card overflow-hidden" data-testid="vbc-measures-card">
            <div className="p-5 border-b border-cn-border">
              <h4 className="font-display font-semibold text-base text-cn-text">Measures</h4>
              <p className="text-xs text-cn-text-muted mt-0.5">Per-measure rate against target. Numerator/denominator computed live.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="vbc-measures-table">
                <thead className="bg-cn-bone-50 border-b border-cn-border text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                  <tr className="text-left">
                    <th className="px-5 py-3">Measure</th>
                    <th className="px-3 py-3">NCQA</th>
                    <th className="px-3 py-3 text-right">Numerator</th>
                    <th className="px-3 py-3 text-right">Denominator</th>
                    <th className="px-3 py-3 text-right">Rate</th>
                    <th className="px-3 py-3 text-right">Target</th>
                    <th className="px-3 py-3">Stars</th>
                    <th className="px-3 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cn-border">
                  {(data.measures || []).map((m) => (
                    <tr key={m.id} className="hover:bg-cn-bone-50/40" data-testid={`vbc-measure-${m.id}`}>
                      <td className="px-5 py-2.5">
                        <p className="font-medium text-cn-text">{m.name}</p>
                        <p className="text-[10px] text-cn-text-muted">{m.category} · {m.direction === 'higher_better' ? '↑ higher is better' : '↓ lower is better'}</p>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-cn-text-muted">{m.ncqa_alias}</td>
                      <td className="px-3 py-2.5 text-right text-xs">{m.numerator}</td>
                      <td className="px-3 py-2.5 text-right text-xs">{m.denominator}</td>
                      <td className="px-3 py-2.5 text-right font-semibold">{m.rate_pct}%</td>
                      <td className="px-3 py-2.5 text-right text-xs text-cn-text-muted">{m.target_pct}%</td>
                      <td className="px-3 py-2.5"><StarRow stars={m.stars} /></td>
                      <td className="px-3 py-2.5">
                        {m.denominator === 0 ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full border font-semibold bg-cn-bone-50 text-cn-text-muted">no data</span>
                        ) : m.meets_target ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full border font-semibold bg-emerald-50 text-emerald-700 border-emerald-200">on target</span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded-full border font-semibold bg-red-50 text-red-700 border-red-200">below target</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </DashboardShell>
  );
}

function KpiTile({ label, value, icon: Icon, tone = 'neutral', testid }) {
  const iconCls = {
    neutral: 'text-cn-primary', amber: 'text-amber-600', blue: 'text-blue-600',
    emerald: 'text-emerald-600', red: 'text-red-600',
  }[tone];
  return (
    <div className="cn-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</span>
        <Icon className={`w-4 h-4 ${iconCls}`} />
      </div>
      <p className="font-display font-bold text-3xl text-cn-text dark:text-white">{value}</p>
    </div>
  );
}
